"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "./utils";

// ── Variant context ─────────────────────────────────────────────────────
//
// Two visual treatments of tabs:
//
//   default   — shadcn segmented pill (muted rail, white pill highlights
//               the active tab). Compact and good for fixed pick sets.
//   underline — labels in a row with a thin bar under the active tab,
//               sitting on a hairline border. Reads as a content section
//               header rather than a control; good for working
//               surfaces.
//
// Variant is set on `TabsList` and propagates to every `TabsTrigger`
// inside it via context. Children render whatever inner content makes
// sense — count chips, icons, etc. — and can opt into active-state
// styling via Tailwind's `group-data-[state=active]:` selectors against
// the trigger root (TabsTrigger sets `group` on itself).

type TabsVariant = "default" | "underline";
const TabsVariantContext = React.createContext<TabsVariant>("default");

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function TabsList({
  variant = "default",
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  variant?: TabsVariant;
}) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={variant}
        className={cn(
          variant === "underline"
            ? [
                // Single-row layout; horizontal scroll for overflow rather
                // than wrap (modern pattern — Stripe/Slack/GitHub style).
                "flex items-end gap-1 w-full overflow-x-auto",
                // Softer border colour than other elements that share the
                // hairline pattern — keeps the underline rail from
                // competing with surrounding card/section chrome.
                "border-b border-border/60",
                // Hide the scrollbar visually; content still scrolls via
                // wheel/swipe/drag and "tabs clip on the right" is the
                // discoverability cue. Same pattern as Twitter/X tabs and
                // mobile Safari. Avoids a stray scrollbar appearing on
                // short tab strips where browsers sometimes round
                // overflow-x-auto into the visible state for the sake of
                // 1px of border-bottom.
                "[scrollbar-width:none]",
                "[&::-webkit-scrollbar]:hidden",
              ].join(" ")
            : "bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-xl p-[3px]",
          className,
        )}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext);

  if (variant === "underline") {
    return (
      <TabsPrimitive.Trigger
        data-slot="tabs-trigger"
        className={cn(
          // Layout — sit flush at the bottom of the TabsList. We
          // intentionally do NOT pull the trigger past the rail with a
          // negative margin (that gives a slicker "underline replaces
          // the rail" effect but causes 1px of vertical overflow inside
          // `overflow-x-auto`, which browsers escalate to vertical
          // scroll). The underline at `bottom-0` of the trigger sits
          // just above the rail — visually clean and stable.
          "group relative inline-flex items-center gap-1.5 px-3 py-2",
          "text-sm font-medium tracking-tight whitespace-nowrap",
          // Inactive vs active colour + hover affordance on inactive
          "text-muted-foreground hover:text-foreground",
          "data-[state=active]:text-foreground",
          "transition-colors",
          // Focus ring (subtle, doesn't conflict with the underline)
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm",
          "disabled:pointer-events-none disabled:opacity-50",
          // Animated underline via ::after — fades in/out on state
          // change. Positioned at `bottom-0` (inside the trigger box) so
          // it doesn't extend below the TabsList, which would otherwise
          // trigger vertical scroll inside the overflow-x-auto rail. The
          // 2px-tall bar sits flush with the trigger's bottom edge —
          // visually right where the rail border sits — so the active
          // tab's underline replaces the rail under it.
          "after:content-[''] after:absolute after:inset-x-2 after:bottom-0 after:h-0.5",
          "after:bg-transparent after:transition-colors after:duration-200",
          "data-[state=active]:after:bg-foreground",
          // Icons in triggers
          "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className,
        )}
        {...props}
      />
    );
  }

  // Default — shadcn's segmented pill (unchanged).
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "data-[state=active]:bg-card dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-xl border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

// ── TabsTriggerCount ────────────────────────────────────────────────────
//
// Count chip styled to match either tab variant. Renders as a small
// rounded pill with tabular-nums background fill — replaces the
// "(2)" parens-text pattern. Reads its active state by sitting inside
// a `group` TabsTrigger and using `group-data-[state=active]:`.

function TabsTriggerCount({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center min-w-[1.25rem] h-[1.125rem] px-1.5",
        "text-[10px] font-medium tabular-nums rounded-full transition-colors",
        "bg-muted text-muted-foreground",
        "group-data-[state=active]:bg-foreground/10 group-data-[state=active]:text-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsTriggerCount };
